import React, { useState, useEffect, useContext, useMemo } from "react";
import { Plus, UserCheck, Users, X, Info } from "lucide-react";
import { FieldConfigurationContext } from "../../../../lib/context/FieldConfigurationContext";
import SearchBar from "../components/SearchBar";
import SortableTable from "../components/SortableTable";
import PaginationControls from "../components/PaginationControls";
import ExportButton from "../components/ExportButton";
import LicenseErrorModal from "../components/LicenseErrorModal";
import Button from "../../../common/Button";
import Validation from "../../../common/Validation";
import { groupUsers } from "../userUtils";
import "./UsersList.css";

export default function UsersList({
  onUserSelect,
  onAddUser,
  capabilityFilter = null,
  onClearFilter = null,
}) {
  const {
    users,
    roles,
    companies,
    fetchUsers,
    fetchRoles,
    fetchLicenses,
    bulkUpdateUsers,
    bulkInviteUsers,
  } = useContext(FieldConfigurationContext);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [hideInactive, setHideInactive] = useState(false);
  const [paginationConfig, setPaginationConfig] = useState({
    currentPage: 1,
    pageSize: 10,
  });
  const [error, setError] = useState("");
  const [licenseErrors, setLicenseErrors] = useState([]);
  const [licenseErrorDetails, setLicenseErrorDetails] = useState([]);
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [licenseErrorRoleUuid, setLicenseErrorRoleUuid] = useState(null);
  const [success, setSuccess] = useState("");

  // Fetch roles and users on mount (roles first to ensure proper name resolution)
  useEffect(() => {
    const fetchData = async () => {
      await fetchRoles();
      await fetchUsers();
    };
    fetchData();
  }, [fetchRoles, fetchUsers]);

  // Auto-enable hide inactive when capability filter is applied
  useEffect(() => {
    if (capabilityFilter) {
      setHideInactive(true);
    }
  }, [capabilityFilter]);

  // Build roles map for role name lookup (memoized)
  const rolesMap = useMemo(() => {
    return roles.data.reduce((map, role) => {
      map[role.uuid] = role;
      return map;
    }, {});
  }, [roles.data]);

  // Enrich users with resolved role names (memoized)
  const enrichedUsers = useMemo(() => {
    return users.data.map((user) => {
      const role = rolesMap[user.roleUuid];
      return {
        ...user,
        role: role?.name || "Unknown Role", // Always resolve role name from roleUuid
      };
    });
  }, [users.data, rolesMap]);

  // Filter users based on search term and inactive filter
  const filteredUsers = useMemo(() => {
    let filtered = enrichedUsers;

    // Apply capability filter first (from license navigation)
    if (capabilityFilter && roles.data.length > 0) {
      // Filter users based on their role's capability
      filtered = filtered.filter((user) => {
        const role = rolesMap[user.roleUuid];
        if (!role) return false; // Exclude users with missing roles

        switch (capabilityFilter) {
          case "time":
            return role.enableTime === true;
          case "expense":
            return role.enableExpense === true;
          case "projectManagement":
            return role.enableProjectManagement === true;
          default:
            return true;
        }
      });
    }

    // Apply inactive filter
    if (hideInactive) {
      filtered = filtered.filter((user) => user.active !== false);
    }

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((user) => {
        const fullName = `${user.firstName || ""} ${
          user.lastName || ""
        }`.toLowerCase();
        return (
          fullName.includes(searchLower) ||
          user.email?.toLowerCase().includes(searchLower) ||
          user.role?.toLowerCase().includes(searchLower)
        );
      });
    }

    return filtered;
  }, [
    enrichedUsers,
    capabilityFilter,
    roles.data,
    rolesMap,
    searchTerm,
    hideInactive,
  ]);

  // Detect multi-company mode
  const isMultiCompany = useMemo(
    () => Object.keys(companies.data || {}).length > 1,
    [companies.data]
  );

  // Group users by email when in multi-company mode
  const groupedUsers = useMemo(
    () =>
      isMultiCompany
        ? groupUsers(filteredUsers, companies.data || {})
        : filteredUsers,
    [isMultiCompany, filteredUsers, companies.data]
  );

  // Sort users
  const sortedUsers = useMemo(() => {
    if (!sortConfig.key) return groupedUsers;

    const sorted = [...groupedUsers].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Special handling for name (combine firstName and lastName)
      if (sortConfig.key === "firstName") {
        aValue = `${a.firstName || ""} ${a.lastName || ""}`;
        bValue = `${b.firstName || ""} ${b.lastName || ""}`;
      }

      // Handle null/undefined
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Compare values
      if (typeof aValue === "string") {
        return sortConfig.direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === "number") {
        return sortConfig.direction === "asc"
          ? aValue - bValue
          : bValue - aValue;
      }

      if (typeof aValue === "boolean") {
        return sortConfig.direction === "asc"
          ? aValue === bValue
            ? 0
            : aValue
            ? -1
            : 1
          : aValue === bValue
          ? 0
          : aValue
          ? 1
          : -1;
      }

      return 0;
    });

    return sorted;
  }, [groupedUsers, sortConfig]);

  // Paginate users
  const paginatedUsers = useMemo(() => {
    const startIndex =
      (paginationConfig.currentPage - 1) * paginationConfig.pageSize;
    const endIndex = startIndex + paginationConfig.pageSize;
    return sortedUsers.slice(startIndex, endIndex);
  }, [sortedUsers, paginationConfig]);

  // Reset pagination when filters change
  useEffect(() => {
    setPaginationConfig((prev) => ({ ...prev, currentPage: 1 }));
  }, [groupedUsers.length]);

  // Handle sort
  const handleSort = (columnKey) => {
    setSortConfig((prev) => ({
      key: columnKey,
      direction:
        prev.key === columnKey && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  // Handle user selection (supports grouped rows with multiple UUIDs)
  const handleUserSelection = (user, checked) => {
    const rowUuids = user.uuids || [user.uuid];
    if (checked) {
      setSelectedUsers((prev) => [
        ...prev,
        ...rowUuids.filter((id) => !prev.includes(id)),
      ]);
    } else {
      setSelectedUsers((prev) => prev.filter((id) => !rowUuids.includes(id)));
    }
  };

  // Handle select all (select only current page)
  const handleSelectAll = (checked) => {
    if (checked) {
      const allUuids = paginatedUsers.flatMap(
        (user) => user.uuids || [user.uuid]
      );
      setSelectedUsers(allUuids);
    } else {
      setSelectedUsers([]);
    }
  };

  // Handle bulk enable
  const handleBulkEnable = async () => {
    setError("");
    setLicenseErrors([]);
    setLicenseErrorDetails([]);
    setSuccess("");

    const updates = selectedUsers.map((uuid) => ({
      uuid,
      data: { enabled: true },
    }));

    const result = await bulkUpdateUsers(updates);
    if (result.success) {
      setSuccess(`Successfully enabled ${result.data.successful} user(s)`);
      setSelectedUsers([]);
      setTimeout(() => setSuccess(""), 5000);
    } else if (
      result.status === 403 &&
      result.errors &&
      result.errors.length > 0
    ) {
      // Handle license limit errors with modal
      // Try to get the role UUID from the first selected user
      const firstSelectedUser = users.data.find((u) =>
        selectedUsers.includes(u.uuid)
      );
      const roleUuid = firstSelectedUser?.roleUuid || null;

      setError(
        result.error || "Insufficient license seats to enable selected users."
      );
      setLicenseErrors(result.errors);
      setLicenseErrorDetails(result.details || []);
      setLicenseErrorRoleUuid(roleUuid);
      setShowLicenseModal(true);
    } else {
      setError(result.error || "Failed to enable users. Please try again.");
    }
  };

  // Handle bulk invite
  const handleBulkInvite = async () => {
    setError("");
    setLicenseErrors([]);
    setSuccess("");

    const result = await bulkInviteUsers(selectedUsers);
    if (result.success) {
      setSuccess(
        `Successfully sent invites to ${result.data.successful} user(s)`
      );
      setSelectedUsers([]);
      setTimeout(() => setSuccess(""), 5000);
    } else {
      setError(result.error || "Failed to send invites. Please try again.");
    }
  };

  // Check if any selected users are inactive (to show Enable button)
  const hasInactiveSelected = useMemo(() => {
    if (selectedUsers.length === 0) return false;
    return selectedUsers.some((uuid) => {
      const user = users.data.find((u) => u.uuid === uuid);
      return user && user.active === false;
    });
  }, [selectedUsers, users.data]);

  // Table columns configuration
  const columns = [
    {
      key: "firstName",
      label: "Name",
      sortable: true,
      render: (user) => (
        <span style={{ fontWeight: 500, color: "rgba(46, 50, 66, 1)" }}>
          {user.firstName} {user.lastName}
          {user.alsoKnownAs?.length > 0 && (
            <span className="user-aka-wrapper">
              <Info size={14} className="user-aka-icon" />
              <span className="user-aka-tooltip">
                Also known as:{" "}
                {user.alsoKnownAs
                  .map((n) => `${n.firstName} ${n.lastName}`)
                  .join(", ")}
              </span>
            </span>
          )}
        </span>
      ),
    },
    {
      key: "email",
      label: "Email",
      sortable: true,
    },
    {
      key: "role",
      label: "Role",
      sortable: true,
    },
    ...(isMultiCompany
      ? [
          {
            key: "companyNames",
            label: "Companies",
            sortable: false,
            width: "200px",
            render: (user) =>
              user.companyNames?.length > 0 ? (
                <div className="role-company-tags">
                  {user.companyNames.map((name, i) => (
                    <span key={i} className="role-company-tag" title={name}>
                      {name}
                    </span>
                  ))}
                </div>
              ) : (
                "\u2014"
              ),
          },
        ]
      : []),
    {
      key: "active",
      label: "Status",
      sortable: true,
      width: "100px",
      align: "center",
      render: (user, value) => (
        <span
          className={`user-status-badge ${
            value ? "user-status-badge--active" : "user-status-badge--inactive"
          }`}
        >
          {value ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      key: "lastLogin",
      label: "Last Login",
      sortable: true,
      width: "140px",
      render: (user, value) => {
        if (!value) return "Never";
        try {
          const date = new Date(value);
          return date.toLocaleDateString();
        } catch {
          return value;
        }
      },
    },
  ];

  // Export columns configuration
  const exportColumns = [
    {
      key: "firstName",
      label: "Name",
      format: (val, user) => `${user.firstName || ""} ${user.lastName || ""}`,
    },
    { key: "email", label: "Email" },
    { key: "role", label: "Role" },
    ...(isMultiCompany
      ? [
          {
            key: "companyNames",
            label: "Companies",
            format: (val) => (val || []).join(", "),
          },
        ]
      : []),
    {
      key: "active",
      label: "Status",
      format: (val) => (val ? "Active" : "Inactive"),
    },
    {
      key: "lastLogin",
      label: "Last Login",
      format: (val) => {
        if (!val) return "Never";
        try {
          return new Date(val).toLocaleDateString();
        } catch {
          return val;
        }
      },
    },
  ];

  // Custom column for checkbox selection
  const allCurrentPageSelected =
    paginatedUsers.length > 0 &&
    paginatedUsers.every((user) => {
      const rowUuids = user.uuids || [user.uuid];
      return rowUuids.every((id) => selectedUsers.includes(id));
    });

  const selectionColumn = {
    key: "select",
    label: (
      <input
        type="checkbox"
        checked={allCurrentPageSelected}
        onChange={(e) => handleSelectAll(e.target.checked)}
        style={{ width: "16px", height: "16px", cursor: "pointer" }}
        aria-label="Select all users on current page"
      />
    ),
    width: "50px",
    align: "center",
    sortable: false,
    render: (user) => {
      const rowUuids = user.uuids || [user.uuid];
      return (
        <input
          type="checkbox"
          checked={rowUuids.every((id) => selectedUsers.includes(id))}
          onChange={(e) => {
            e.stopPropagation();
            handleUserSelection(user, e.target.checked);
          }}
          onClick={(e) => e.stopPropagation()}
          style={{ width: "16px", height: "16px", cursor: "pointer" }}
        />
      );
    },
  };

  // Combine columns
  const allColumns = [selectionColumn, ...columns];

  return (
    <div className="users-list">
      {/* Error and Success Messages */}
      {error && !showLicenseModal && (
        <div style={{ marginBottom: "16px" }}>
          <Validation className="danger">{error}</Validation>
        </div>
      )}
      {success && (
        <div style={{ marginBottom: "16px" }}>
          <Validation className="success">{success}</Validation>
        </div>
      )}

      {/* Capability Filter Banner */}
      {capabilityFilter && (
        <div className="users-list__filter-banner">
          <div className="users-list__filter-content">
            <span className="users-list__filter-label">
              Filtered by capability:
            </span>
            <span className="users-list__filter-value">
              {capabilityFilter === "time" && "Time Entry Users"}
              {capabilityFilter === "expense" && "Expense Users"}
              {capabilityFilter === "projectManagement" &&
                "Project Management Users"}
            </span>
            <span className="users-list__filter-count">
              ({filteredUsers.length}{" "}
              {filteredUsers.length === 1 ? "user" : "users"})
            </span>
          </div>
          {onClearFilter && (
            <button
              onClick={onClearFilter}
              className="users-list__filter-dismiss"
              aria-label="Clear capability filter"
            >
              <X size={16} />
            </button>
          )}
        </div>
      )}

      {/* Header */}
      <div className="users-list__header">
        <div className="users-list__header-top">
          <h2 className="users-list__title">Users</h2>
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search users..."
          />
          <label className="users-list__filter-checkbox">
            <input
              type="checkbox"
              checked={hideInactive}
              onChange={(e) => {
                setHideInactive(e.target.checked);
                setSelectedUsers([]); // Clear selection when filter changes
              }}
            />
            <span>Hide inactive users</span>
          </label>
          <div className="users-list__actions">
            {hasInactiveSelected && (
              <Button className="button--success" onClick={handleBulkEnable}>
                <UserCheck size={18} style={{ marginRight: "6px" }} />
                Enable ({selectedUsers.length})
              </Button>
            )}
            {selectedUsers.length > 0 && (
              <Button className="button--info" onClick={handleBulkInvite}>
                <Users size={18} style={{ marginRight: "6px" }} />
                Invite ({selectedUsers.length})
              </Button>
            )}
            <ExportButton
              data={sortedUsers}
              filename="users_export"
              columns={exportColumns}
              className="button--secondary"
            />
            <Button className="button--primary" onClick={onAddUser}>
              <Plus size={18} style={{ marginRight: "6px" }} />
              Add User
            </Button>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {users.loading && (
        <div className="users-list__loading">
          <p>Loading users...</p>
        </div>
      )}

      {/* Error State */}
      {users.error && (
        <div className="users-list__error">
          <p>Error loading users: {users.error}</p>
          <Button onClick={() => fetchUsers(true)}>Retry</Button>
        </div>
      )}

      {/* Table */}
      {!users.loading && !users.error && (
        <>
          <SortableTable
            data={paginatedUsers}
            columns={allColumns}
            sortConfig={sortConfig}
            onSort={handleSort}
            onRowClick={onUserSelect}
            emptyMessage="No users found. Click 'Add User' to create your first user."
          />
          <PaginationControls
            currentPage={paginationConfig.currentPage}
            pageSize={paginationConfig.pageSize}
            totalItems={sortedUsers.length}
            onPageChange={(newPage) => {
              setPaginationConfig((prev) => ({
                ...prev,
                currentPage: newPage,
              }));
            }}
          />
        </>
      )}

      {/* License Error Modal */}
      <LicenseErrorModal
        isOpen={showLicenseModal}
        onClose={async () => {
          setShowLicenseModal(false);
          setSelectedUsers([]); // Clear selection when modal closes
          // Refresh data in case changes were made through the modal
          await fetchUsers(true);
          await fetchLicenses(true);
        }}
        roleUuid={licenseErrorRoleUuid}
        errorMessage={error}
        details={licenseErrorDetails}
        context="user"
      />
    </div>
  );
}
